/**
 * Colour arithmetic.
 *
 * Everything in this file is a pure function of hex strings. It exists because a tenant
 * supplies its own brand colour at runtime (docs/DECISIONS.md, "Branding — runtime by
 * default"), and an arbitrary colour dropped onto our surfaces destroys contrast. The
 * design system therefore never uses a supplied colour as given: it derives a legible
 * pair from it, and the derivation is arithmetic that can be tested exhaustively.
 *
 * The contrast formula is WCAG 2.2's, unchanged: relative luminance from linearised
 * sRGB, ratio `(lighter + 0.05) / (darker + 0.05)`.
 */

/** A colour in 8-bit sRGB. Channels are integers in `[0, 255]`. */
export type Rgb = {
  readonly red: number
  readonly green: number
  readonly blue: number
}

/** A colour in HSL. `hue` is degrees in `[0, 360)`; `saturation` and `lightness` are `[0, 1]`. */
export type Hsl = {
  readonly hue: number
  readonly saturation: number
  readonly lightness: number
}

/** Three- or six-digit hex, with or without the leading `#`. Nothing else parses. */
const HEX_PATTERN = /^#?(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

/**
 * WCAG 2.2 AA for normal text, and the floor this design system guarantees for every
 * text-on-surface pair it produces — including after a tenant swaps the brand colour.
 */
export const CONTRAST_TEXT = 4.5

/** WCAG 2.2 AA for large text (>= 18.66px bold, or >= 24px). */
export const CONTRAST_LARGE_TEXT = 3

/** WCAG 2.2 AA for user-interface components and graphical objects: borders, focus rings. */
export const CONTRAST_NON_TEXT = 3

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value))
}

function expandShorthand(digits: string): string {
  if (digits.length !== 3) {
    return digits
  }
  return digits.replace(/./g, (digit) => digit + digit)
}

/**
 * Parse a hex colour, or return `undefined` when the string is not one.
 *
 * Returning `undefined` rather than throwing is deliberate: the caller is usually
 * handling a value that arrived over the network from a tenant's configuration, and the
 * right answer to a malformed one is to fall back to the default brand, not to crash the
 * app on launch.
 */
export function parseHex(value: string): Rgb | undefined {
  if (!HEX_PATTERN.test(value)) {
    return undefined
  }
  const digits = expandShorthand(value.replace('#', ''))

  return {
    red: Number.parseInt(digits.slice(0, 2), 16),
    green: Number.parseInt(digits.slice(2, 4), 16),
    blue: Number.parseInt(digits.slice(4, 6), 16),
  }
}

function channelToHex(channel: number): string {
  return Math.round(clamp(channel, 0, 255))
    .toString(16)
    .padStart(2, '0')
}

/** Render a colour as `#rrggbb`, lowercase. */
export function toHex(color: Rgb): string {
  return `#${channelToHex(color.red)}${channelToHex(color.green)}${channelToHex(color.blue)}`
}

function linearise(channel: number): number {
  const normalised = channel / 255
  return normalised <= 0.04045 ? normalised / 12.92 : ((normalised + 0.055) / 1.055) ** 2.4
}

/** WCAG relative luminance, `[0, 1]`. */
export function relativeLuminance(color: Rgb): number {
  return (
    0.2126 * linearise(color.red) + 0.7152 * linearise(color.green) + 0.0722 * linearise(color.blue)
  )
}

/**
 * WCAG contrast ratio between two colours, `[1, 21]`.
 *
 * Unparseable input is treated as the worst case (ratio 1) rather than throwing, so a
 * malformed colour can never *pass* a contrast assertion by accident.
 */
export function contrastRatio(foreground: string, background: string): number {
  const first = parseHex(foreground)
  const second = parseHex(background)
  if (first === undefined || second === undefined) {
    return 1
  }
  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second))
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second))

  return (lighter + 0.05) / (darker + 0.05)
}

export function rgbToHsl(color: Rgb): Hsl {
  const red = color.red / 255
  const green = color.green / 255
  const blue = color.blue / 255
  const maximum = Math.max(red, green, blue)
  const minimum = Math.min(red, green, blue)
  const lightness = (maximum + minimum) / 2
  const span = maximum - minimum

  if (span === 0) {
    return { hue: 0, saturation: 0, lightness }
  }

  const saturation = span / (1 - Math.abs(2 * lightness - 1))
  let hue: number
  if (maximum === red) {
    hue = ((green - blue) / span) % 6
  } else if (maximum === green) {
    hue = (blue - red) / span + 2
  } else {
    hue = (red - green) / span + 4
  }
  hue *= 60

  return { hue: hue < 0 ? hue + 360 : hue, saturation, lightness }
}

export function hslToRgb(color: Hsl): Rgb {
  const saturation = clamp(color.saturation, 0, 1)
  const lightness = clamp(color.lightness, 0, 1)
  const hue = ((color.hue % 360) + 360) % 360
  const amplitude = saturation * Math.min(lightness, 1 - lightness)

  // The branchless formulation of the HSL→RGB conversion: `channel(0)`, `channel(8)` and
  // `channel(4)` are red, green and blue. Written this way rather than as a six-way
  // sector table because a table needs an unreachable default arm to satisfy the type
  // checker, and an unreachable arm is an uncoverable branch.
  const channel = (offset: number): number => {
    const position = (offset + hue / 30) % 12
    return lightness - amplitude * Math.max(-1, Math.min(position - 3, 9 - position, 1))
  }

  return {
    red: Math.round(channel(0) * 255),
    green: Math.round(channel(8) * 255),
    blue: Math.round(channel(4) * 255),
  }
}

/**
 * Move a colour's HSL lightness to an absolute value, keeping hue and saturation.
 *
 * Lightness rather than any perceptual space because the guarantee this file makes is
 * about the WCAG ratio, which is measured in sRGB luminance — and sRGB luminance is
 * monotonic in HSL lightness for a fixed hue and saturation, which is what makes the
 * search in `ensureContrast` terminate on an exact answer.
 */
export function withLightness(value: string, lightness: number): string {
  const color = parseHex(value)
  if (color === undefined) {
    return value
  }
  return toHex(hslToRgb({ ...rgbToHsl(color), lightness: clamp(lightness, 0, 1) }))
}

/** A colour's HSL lightness, or 0 when it does not parse. */
export function lightnessOf(value: string): number {
  const color = parseHex(value)
  return color === undefined ? 0 : rgbToHsl(color).lightness
}

/** Shift a colour's HSL lightness by `delta`, clamped to `[0, 1]`. */
export function shiftLightness(value: string, delta: number): string {
  const color = parseHex(value)
  if (color === undefined) {
    return value
  }
  return withLightness(value, rgbToHsl(color).lightness + delta)
}

/**
 * Blend two colours in sRGB. `weight` is how much of `overlay` ends up in the result.
 *
 * Used for tinted surfaces — a status or brand wash over the theme's own surface — so
 * that a tint is a real opaque colour rather than a translucent layer whose contrast
 * depends on whatever happens to be behind it.
 */
export function mix(base: string, overlay: string, weight: number): string {
  const first = parseHex(base)
  const second = parseHex(overlay)
  if (first === undefined || second === undefined) {
    return base
  }
  const amount = clamp(weight, 0, 1)

  return toHex({
    red: first.red + (second.red - first.red) * amount,
    green: first.green + (second.green - first.green) * amount,
    blue: first.blue + (second.blue - first.blue) * amount,
  })
}

/** Express a hex colour as `rgba(...)`, for scrims and other genuinely translucent layers. */
export function withAlpha(value: string, alpha: number): string {
  const color = parseHex(value)
  if (color === undefined) {
    return value
  }
  return `rgba(${color.red}, ${color.green}, ${color.blue}, ${clamp(alpha, 0, 1)})`
}

/** Whichever candidate has the most contrast against `background`. Ties keep the first. */
export function bestContrast(background: string, candidates: readonly string[]): string {
  let winner = candidates[0] ?? '#000000'
  let best = contrastRatio(winner, background)

  for (const candidate of candidates.slice(1)) {
    const ratio = contrastRatio(candidate, background)
    if (ratio > best) {
      winner = candidate
      best = ratio
    }
  }

  return winner
}

const SEARCH_STEPS = 24

/**
 * The lightness closest to the colour's own that reaches `minimum` against `background`,
 * searching in one direction, or `undefined` when even the endpoint cannot reach it.
 */
function searchLightness(
  value: string,
  background: string,
  minimum: number,
  direction: 'lighter' | 'darker',
): string | undefined {
  const endpoint = direction === 'lighter' ? 1 : 0
  if (contrastRatio(withLightness(value, endpoint), background) < minimum) {
    return undefined
  }

  let failing = lightnessOf(value)
  let passing = endpoint
  for (let step = 0; step < SEARCH_STEPS; step += 1) {
    const midpoint = (failing + passing) / 2
    if (contrastRatio(withLightness(value, midpoint), background) >= minimum) {
      passing = midpoint
    } else {
      failing = midpoint
    }
  }

  // `passing` is only ever assigned a lightness whose *rendered eight-bit colour* was
  // measured and passed — the loop tests `withLightness(value, midpoint)`, which is the
  // same call made here. So the answer needs no re-check: rounding cannot have crept in
  // between the measurement and the result. `passing` starts at the endpoint, which the
  // guard above already measured.
  return withLightness(value, passing)
}

/**
 * The nearest colour to `value`, in hue and saturation, that meets `minimum` against
 * `background`.
 *
 * This is the guarantee the whole theme rests on: no matter what a tenant supplies, the
 * text drawn in its brand colour is legible on our surfaces. When the colour already
 * passes it is returned untouched — a tenant that picked a sensible colour sees exactly
 * the colour it picked.
 */
export function ensureContrast(value: string, background: string, minimum: number): string {
  if (parseHex(value) === undefined || parseHex(background) === undefined) {
    return value
  }
  if (contrastRatio(value, background) >= minimum) {
    return value
  }

  const darker = searchLightness(value, background, minimum, 'darker')
  const lighter = searchLightness(value, background, minimum, 'lighter')
  if (darker === undefined) {
    return lighter ?? bestContrast(background, ['#000000', '#ffffff'])
  }
  if (lighter === undefined) {
    return darker
  }

  // Both directions work: keep the smaller move, so a colour that is nearly legible is
  // nudged rather than flipped to the other end of the scale. Ties go darker, which on a
  // light surface is the direction that also keeps the hue recognisable.
  const original = lightnessOf(value)
  const lighterDistance = Math.abs(original - lightnessOf(lighter))
  const darkerDistance = Math.abs(original - lightnessOf(darker))

  return lighterDistance < darkerDistance ? lighter : darker
}
